import type { PersistencePayload } from 'apiConnector/dashboard/model';
import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import type { Collection } from 'state/utils/types';
import type { QuoteWorkspaceType } from 'state/workspaces/model';

import type { FilterType, StatusFilter } from './filter/filter.model';
import type { ReloadedTileData } from './reloadedTiles/reloadedTiles.model';
import type { GroupColor, TileMetadata } from './tiles/tiles.model';

export type DashboardAction =
  | DashboardTileCreating
  | DashboardTileCreated
  | DashboardTileDuplicating
  | DashboardTileDuplicated
  | DashboardTileRemoved
  | DashboardTilePinToggled
  | DashboardFilterTypeChanged
  | DashboardByStatusFilterChanged
  | DashboardByClientFilterChanged
  | DashboardByPinnedFilterChanged
  | DashboardPinnedGroupRenamed
  | DashboardPinnedGroupColorChanged
  | DashboardQuickSearchTermChanged
  | DashboardPersistenceRequested
  | DashboardPersisted
  | DashboardPersistenceFailed
  | DashboardLoading
  | DashboardLoaded
  | DashboardLoadingFailed
  | DashboardObsoleteQuotesDeleted
  | DashboardTileGroupChanged;

export function dashboardTileCreating(reconciliationId: string, workspace: QuoteWorkspaceType) {
  return {
    type: 'DASHBOARD_TILE_CREATING' as const,
    reconciliationId,
    workspace,
  };
}
export type DashboardTileCreating = ReturnType<typeof dashboardTileCreating>;

export function dashboardTileCreated(quoteId: string) {
  return {
    type: 'DASHBOARD_TILE_CREATED' as const,
    quoteId,
  };
}
export type DashboardTileCreated = ReturnType<typeof dashboardTileCreated>;

export function dashboardTileDuplicating(
  reconciliationId: string,
  originalQuoteId: string,
  workspace: QuoteWorkspaceType,
) {
  return {
    type: 'DASHBOARD_TILE_DUPLICATING' as const,
    reconciliationId,
    originalQuoteId,
    workspace,
  };
}

export type DashboardTileDuplicating = ReturnType<typeof dashboardTileDuplicating>;

export function dashboardTileDuplicated(quoteId: string, originalQuoteId: string, originalQuoteMetadata: TileMetadata) {
  return {
    type: 'DASHBOARD_TILE_DUPLICATED' as const,
    quoteId,
    originalQuoteId,
    originalQuoteMetadata,
  };
}

export type DashboardTileDuplicated = ReturnType<typeof dashboardTileDuplicated>;

export function dashboardTileRemoved(quoteId: string) {
  return {
    type: 'DASHBOARD_TILE_REMOVED' as const,
    quoteId,
  };
}
export type DashboardTileRemoved = ReturnType<typeof dashboardTileRemoved>;

export function dashboardTilePinToggled(quoteId: string) {
  return {
    type: 'DASHBOARD_TILE_PIN_TOGGLED' as const,
    quoteId,
  };
}

export type DashboardTilePinToggled = ReturnType<typeof dashboardTilePinToggled>;

export function dashboardFilterTypeChanged(filterType: FilterType) {
  return {
    type: 'DASHBOARD_FILTER_TYPE_CHANGED' as const,
    filterType,
  };
}
export type DashboardFilterTypeChanged = ReturnType<typeof dashboardFilterTypeChanged>;

export function dashboardByStatusFilterChanged(status: StatusFilter) {
  return {
    type: 'DASHBOARD_BY_STATUS_FILTER_CHANGED' as const,
    status,
  };
}
export type DashboardByStatusFilterChanged = ReturnType<typeof dashboardByStatusFilterChanged>;

export function dashboardByClientFilterChanged(client?: string, product?: string) {
  return {
    type: 'DASHBOARD_BY_CLIENT_FILTER_CHANGED' as const,
    client,
    product,
  };
}
export type DashboardByClientFilterChanged = ReturnType<typeof dashboardByClientFilterChanged>;

export function dashboardByPinnedFilterChanged(pinnedGroup?: string) {
  return {
    type: 'DASHBOARD_BY_PINNED_FILTER_CHANGED' as const,
    pinnedGroup,
  };
}
export type DashboardByPinnedFilterChanged = ReturnType<typeof dashboardByPinnedFilterChanged>;

export function dashboardPinnedGroupRenamed(previousName: string, newName: string) {
  return {
    type: 'DASHBOARD_PINNED_GROUP_RENAMED' as const,
    previousName,
    newName,
  };
}
export type DashboardPinnedGroupRenamed = ReturnType<typeof dashboardPinnedGroupRenamed>;

export function dashboardPinnedGroupColorChanged(pinnedGroup: string, color?: GroupColor) {
  return {
    type: 'DASHBOARD_PINNED_GROUP_COLOR_CHANGED' as const,
    pinnedGroup,
    color,
  };
}
export type DashboardPinnedGroupColorChanged = ReturnType<typeof dashboardPinnedGroupColorChanged>;

export function dashboardClientTilesDeleted(clientId: string, product?: string) {
  return {
    type: 'DASHBOARD_CLIENT_TILES_DELETED' as const,
    clientId,
    product,
  };
}
export type DashboardClientTilesDeleted = ReturnType<typeof dashboardClientTilesDeleted>;

export function dashboardQuickSearchTermChanged(term?: string) {
  return {
    type: 'DASHBOARD_QUICK_SEARCH_TERM_CHANGED' as const,
    term,
  };
}
export type DashboardQuickSearchTermChanged = ReturnType<typeof dashboardQuickSearchTermChanged>;

export function dashboardPersisted() {
  return {
    type: 'DASHBOARD_PERSISTED' as const,
  };
}
export type DashboardPersisted = ReturnType<typeof dashboardPersisted>;

export function dashboardPersistenceRequested(request: PersistencePayload) {
  return {
    type: 'DASHBOARD_PERSISTENCE_REQUESTED' as const,
    request,
  };
}
export type DashboardPersistenceRequested = ReturnType<typeof dashboardPersistenceRequested>;

export function dashboardPersistenceFailed(error: ApiErrorMessage) {
  return {
    type: 'DASHBOARD_PERSISTENCE_FAILED' as const,
    error,
  };
}
export type DashboardPersistenceFailed = ReturnType<typeof dashboardPersistenceFailed>;

export function dashboardLoading() {
  return {
    type: 'DASHBOARD_LOADING' as const,
  };
}
export type DashboardLoading = ReturnType<typeof dashboardLoading>;

export function dashboardLoaded(
  quoteIds: string[],
  metadata: Collection<TileMetadata>,
  tiles: Collection<ReloadedTileData>,
) {
  return {
    type: 'DASHBOARD_LOADED' as const,
    quoteIds,
    metadata,
    tiles,
  };
}
export type DashboardLoaded = ReturnType<typeof dashboardLoaded>;

export function dashboardLoadingFailed(error: ApiErrorMessage) {
  return {
    type: 'DASHBOARD_LOADING_FAILED' as const,
    error,
  };
}
export type DashboardLoadingFailed = ReturnType<typeof dashboardLoadingFailed>;

export function dashboardTileGroupChanged(quoteId: string, groupName?: string, color?: GroupColor) {
  return {
    type: 'DASHBOARD_TILE_GROUP_CHANGED' as const,
    quoteId,
    groupName,
    color,
  };
}

export type DashboardTileGroupChanged = ReturnType<typeof dashboardTileGroupChanged>;

export function dashboardObsoleteQuotesDeleted(quoteIds: string[]) {
  return {
    type: 'DASHBOARD_OBSOLETE_QUOTES_DELETED' as const,
    quoteIds,
  };
}

export type DashboardObsoleteQuotesDeleted = ReturnType<typeof dashboardObsoleteQuotesDeleted>;
